import { useEffect, useState } from "react";
import styled from "styled-components";
import { State } from "../../types";
import FullAvailability from "./FullAvailability/FullAvailability";
import AvailabilityWithinTwoHours from "./AvailabilityWithinTwoHours/AvailabilityWithinTwoHours";
import OtherVenuesAvailabilityPage from "./OtherVenuesAvailabilityPage/OtherVenuesAvailabilityPage";
import AvailabilityOutsideTwoHours from "./AvailabilityOutsideTwoHours/AvailabilityOutsideTwoHours";
import DateHelper from "../../helpers/DateHelper";
import {
  AvailabilitySearchResponseToTimeSlotList,
  TimeSlot,
} from "../../types/TimeSlot";
import { CrossSellVenue } from "../../domain_objects/CrossSellSetupResponse";
import WidgetService from "../../services/WidgetService";
import { AvailabilityType } from "../../enums/AvailbilityType";
import mockWidgetService from "../../mocks/mockWidgetService";
import {
  BookingSummary,
  Button,
  IconButton,
  Logo,
  Subtitle,
  Title,
  ReturnTableNotice,
} from "../../components";
import { defaultThemeStyles } from "../../mocks";
import { useTranslation } from "react-i18next";
import { AvailabilitySearchResponse } from "../../domain_objects/AvailabilitySearchResponse";
import { ArrowLeftSolid, MapPinOutline } from "../../assets/icons";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 32px auto 32px;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0;
`;

const BodyContainer = styled.div`
  margin-bottom: 15px;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  flex-grow: 1;
`;

const SpacedSubtitlesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MessageLabel = styled.div`
  font-weight: 500;
`;

export type SelectTimePageProps = {
  timeSlots: TimeSlot[];
  state: State;
  setState: (payload: Partial<State>) => void;
  crossSellVenues?: CrossSellVenue[];
  nextPage: (payload: Partial<State>) => void;
  prevPage: (payload: Partial<State>) => void;
  widgetService: WidgetService | mockWidgetService;
  venueSearchLimit: number;
};

export const SelectTimePage: React.FC<SelectTimePageProps> = ({
  timeSlots,
  state,
  setState,
  nextPage,
  prevPage,
  widgetService,
  venueSearchLimit,
}) => {
  const { bookingDetails, initialTimeSlot } = state;
  const { t } = useTranslation();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    initialTimeSlot || null
  );

  useEffect(() => {
    if (state.availabilityType === "full" && selectedTimeSlot === null) {
      const sameTimeSlot = timeSlots.find((t) => {
        if (state.initialTimeSlot) {
          return (
            DateHelper.sliceTimeSlot(t) ===
              DateHelper.sliceTimeSlot(state.initialTimeSlot) &&
            t.MicrositeName === state.initialTimeSlot!.MicrositeName
          );
        }
        return false;
      });
      setSelectedTimeSlot(sameTimeSlot!);
    }
  }, [
    state.availabilityType,
    selectedTimeSlot,
    state.initialTimeSlot,
    timeSlots,
  ]);

  const [isLoading, setIsLoading] = useState(true);
  const [isErrored, setIsErrored] = useState(false);

  useEffect(() => {
    const fetchValues = async (searchVenues: CrossSellVenue[]) => {
      const fetchAvailability = async (venue: CrossSellVenue) => {
        const availability = await widgetService.availabilitySearch(
          state.hostname!,
          venue.MicrositeName,
          bookingDetails.selectedDate,
          bookingDetails.partySize,
          state.channelCode || "",
          AvailabilityType.Reservation
        );
        venue.MicrositeName;
        const timeSlotList = AvailabilitySearchResponseToTimeSlotList({
          ...availability,
          MicrositeName: venue.MicrositeName,
        });

        const twoHourTimeSlots = DateHelper.getTwoHourTimeSlots(
          timeSlotList,
          state.initialTimeSlot!
        );

        if (twoHourTimeSlots.length == 0) {
          availability.TimeSlots = [];
          availability.Promotions = [];
        } else {
          availability.TimeSlots = availability.TimeSlots.filter((ts) =>
            twoHourTimeSlots.find((t) => ts.TimeSlot === t.TimeSlot.TimeSlot)
          );
        }
        return availability;
      };

      const results: Array<AvailabilitySearchResponse> = [];
      for (let i = 0; i < searchVenues.length; i++) {
        const availability = await fetchAvailability(searchVenues[i]);
        results.push(availability);
      }

      const searchedCrossSellVenuesLength =
        state.searchedCrossSellVenues.length + results.length;
      //If there are results with no availability, fetch more venues
      const shouldAddMore =
        results.filter((r) => r.TimeSlots.length == 0).length > 0 &&
        searchedCrossSellVenuesLength <
          state.crossSellSetup!.CrossSellVenues.length;

      const venuesToAdd = state.crossSellSetup?.CrossSellVenues.slice(
        searchedCrossSellVenuesLength,
        results.filter((r) => r.TimeSlots.length == 0).length +
          searchedCrossSellVenuesLength
      );
      if (shouldAddMore) {
        setState({
          availableCrossSellVenues: [
            ...state.availableCrossSellVenues,
            ...(venuesToAdd || []),
          ],
        });
      }
      setState({
        searchedCrossSellVenues: [
          ...state.searchedCrossSellVenues,
          ...results.map((r, i) => {
            return {
              CrossSellVenue: searchVenues[i],
              AvailabilitySearchResponse: r!,
            };
          }),
        ],
        availableCrossSellVenues:
          shouldAddMore && venuesToAdd
            ? state.availableCrossSellVenues.concat(venuesToAdd)
            : state.availableCrossSellVenues,
      });
    };
    if (
      state.availableCrossSellVenues.length >
      state.searchedCrossSellVenues.length
    ) {
      const searchVenues = state.availableCrossSellVenues.filter((v) => {
        return !state.searchedCrossSellVenues.find(
          (csv) => csv.CrossSellVenue.MicrositeName === v.MicrositeName
        );
      });
      if (searchVenues.length > 0) {
        fetchValues(searchVenues);
      }
    }
  }, [
    bookingDetails.partySize,
    bookingDetails.selectedDate,
    setState,
    state.availableCrossSellVenues,
    state.channelCode,
    state.crossSellSetup,
    state.hostname,
    state.initialTimeSlot,
    state.searchedCrossSellVenues,
    widgetService,
  ]);

  useEffect(() => {
    if (
      state.searchedCrossSellVenues.length > 0 &&
      state.searchedCrossSellVenues.length ==
        state.availableCrossSellVenues.length
    ) {
      setIsLoading(false);
    }
  }, [
    state.availableCrossSellVenues.length,
    state.searchedCrossSellVenues.length,
  ]);

  const setTitleText = () => {
    return state.availabilityType === "otherVenues"
      ? "Availability at our other venues"
      : "Select a time";
  };

  const completePage = async () => {
    if (!selectedTimeSlot || !selectedTimeSlot.MicrositeName) {
      setIsErrored(true);
    }
    if (selectedTimeSlot && selectedTimeSlot.MicrositeName) {
      const isCrossSellChosen =
        state.micrositeName !== selectedTimeSlot.MicrositeName;
      const updatedBookingDetails = {
        ...bookingDetails,
        timeSlot: selectedTimeSlot,
      };

      let customField;
      if (isCrossSellChosen) {
        const crossSellSetup = await widgetService.getRestaurantSetup(
          state.hostname!,
          updatedBookingDetails.timeSlot.MicrositeName!,
          updatedBookingDetails.selectedDate,
          state.channelCode!
        );
        customField = crossSellSetup.CustomField;
      } else {
        customField = state.crossSellSetup!.CustomField;
      }

      nextPage({
        ...state,
        isCrossSellChosen,
        bookingDetails: updatedBookingDetails,
        customField,
      });
    }
  };

  const getTimeSlotString = () => {
    if (selectedTimeSlot && selectedTimeSlot.MicrositeName != null) {
      const timeSlotString = DateHelper.sliceTimeSlot(selectedTimeSlot!);
      return timeSlotString;
    }
    return "";
  };

  const goBack = () => {
    if (state.availabilityType === "otherVenues")
      setState({
        ...state,
        availabilityType: "partialWithinTwoHours",
      });
    else
      prevPage({
        ...state,
        bookingDetails: { ...bookingDetails, timeSlot: undefined },
        availabilityResponses: [],
        availabilityType: null,
        searchedCrossSellVenues: [],
        availableCrossSellVenues: [],
      });
  };

  const loadMore = () => {
    setIsLoading(true);
    setState({
      availableCrossSellVenues: [
        ...state.availableCrossSellVenues,
        ...state.crossSellSetup!.CrossSellVenues.slice(
          state.searchedCrossSellVenues.length,
          state.searchedCrossSellVenues.length + venueSearchLimit
        ),
      ],
    });
  };

  const getSpacedSubtitlesContainer = () => {
    if (
      (selectedTimeSlot?.MicrositeName &&
        state.micrositeName !== selectedTimeSlot?.MicrositeName) ||
      (selectedTimeSlot?.TimeSlot.IsLeaveTimeRequired &&
        selectedTimeSlot?.TimeSlot.LeaveTime)
    ) {
      return (
        <SpacedSubtitlesContainer>
          {selectedTimeSlot?.MicrositeName &&
            state.micrositeName !== selectedTimeSlot?.MicrositeName && (
              <Subtitle
                icon={MapPinOutline}
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              >
                <MessageLabel>
                  {t("NewLocation")}{" "}
                  <strong>
                    {
                      state.crossSellSetup?.CrossSellVenues.find(
                        (csv) =>
                          csv.MicrositeName == selectedTimeSlot!.MicrositeName
                      )?.Name
                    }
                  </strong>
                </MessageLabel>
              </Subtitle>
            )}
          {selectedTimeSlot?.TimeSlot.IsLeaveTimeRequired &&
            selectedTimeSlot?.TimeSlot.LeaveTime && (
              <ReturnTableNotice
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
                leaveTime={selectedTimeSlot.TimeSlot.LeaveTime}
              />
            )}
        </SpacedSubtitlesContainer>
      );
    }
    return null;
  };

  return (
    <StyledContainer>
      <Logo styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles} />
      <StyledHeader>
        <IconButton
          ariaLabel="Previous"
          icon={ArrowLeftSolid}
          onClick={goBack}
          styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
          dataId="back-button"
        />
        <StyledTitle
          styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
        >
          {setTitleText()}
        </StyledTitle>
      </StyledHeader>
      <BodyContainer>
        {state.availabilityType === "full" && (
          <FullAvailability
            timeSlots={DateHelper.getTwoHourTimeSlots(
              timeSlots,
              state.initialTimeSlot!
            )}
            selectedTimeSlot={selectedTimeSlot}
            setSelectedTimeSlot={setSelectedTimeSlot}
            styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
          />
        )}
        {state.availabilityType === "partialWithinTwoHours" && (
          <AvailabilityWithinTwoHours
            timeSlots={DateHelper.getTwoHourTimeSlots(
              timeSlots,
              state.initialTimeSlot!
            )}
            selectedTimeSlot={selectedTimeSlot}
            setSelectedTimeSlot={setSelectedTimeSlot}
            checkOtherVenues={() =>
              setState({ ...state, availabilityType: "otherVenues" })
            }
            styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
          />
        )}
        {state.availabilityType === "partialOutsideTwoHours" && (
          <AvailabilityOutsideTwoHours
            state={state}
            getMoreAvailableTimes={() => timeSlots}
            selectedTimeSlot={selectedTimeSlot}
            setSelectedTimeSlot={setSelectedTimeSlot}
            venueSearchLimit={venueSearchLimit}
            isLoading={isLoading}
            loadMore={loadMore}
            setState={setState}
          />
        )}
        {state.availabilityType === "otherVenues" && (
          <OtherVenuesAvailabilityPage
            state={state}
            selectedTimeSlot={selectedTimeSlot}
            setBookingTimeSlot={setSelectedTimeSlot}
            isLoading={isLoading}
            loadMore={loadMore}
            setState={setState}
            venueSearchLimit={venueSearchLimit}
          />
        )}
      </BodyContainer>
      <FooterContainer data-id="footer">
        <BookingSummary
          flashBookingSummaryPanel={state.flashBookingSummaryPanel || false}
          date={bookingDetails.selectedDate}
          numberOfCovers={bookingDetails.partySize}
          timeslot={getTimeSlotString()}
          styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
          isErrored={isErrored}
        />
        {getSpacedSubtitlesContainer()}
        <Button
          styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
          onClick={completePage}
          rightArrowIcon
          dataId="next-button"
        >
          {t("Next")}
        </Button>
      </FooterContainer>
    </StyledContainer>
  );
};

export default SelectTimePage;
